/**
 * The Outage tab — Step 54.
 *
 * What a major incident carries beyond an incident, written DURING the thing
 * itself: how bad, who it hits, where the war room is, and — the field the
 * review cannot reconstruct afterwards — when service came back.
 *
 * Declaring is not done here. It is the type conversion (`Declare a major
 * incident` in the rail), which stamps the declaration from the clock,
 * because the one moment nobody is going to stop and fill in a form is the
 * moment the phones start ringing.
 *
 * **Stand down is its own button, not a date field to remember.** One click,
 * stamped now, audited — because it is the boundary the review is gated on
 * and the number every duration in it is measured from. It is also
 * reversible: "it's back" gets called early more often than anyone admits.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Megaphone, Radio, Undo2 } from 'lucide-react';
import { OUTAGE_KINDS, OUTAGE_KIND_LABEL, outageDuration, type OutageKind } from '@itsm/shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Combobox } from '../../components/ui/combobox';
import { Skeleton } from '../../components/ui/skeleton';
import { RichTextEditor } from '../../components/ui/richtext';
import { apiFetch, apiSend } from '../../lib/api';
import { ticketsApi } from './api';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { fullTime } from '../../lib/time';
import { cn } from '../../lib/utils';
import { useConfirm } from '../../lib/confirm';

/** `datetime-local` wants local wall-clock, not an ISO instant. */
const toLocal = (ms: number) => {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
};

export interface MajorIncident {
  outageKind: OutageKind | null;
  businessImpact: string | null;
  impactedLocations: string | null;
  impactedServices: string | null;
  customersImpacted: number | null;
  commsChannel: string | null;
  managerId: string | null;
  managerName?: string | null;
  detectedAt: number | null;
  declaredAt: number | null;
  stoodDownAt: number | null;
}

export function OutageTab({ id, canEdit }: { id: string; canEdit: boolean }) {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.tickets.majorIncident(id),
    queryFn: () => apiFetch<{ entry: MajorIncident | null }>(`/api/tickets/${id}/major-incident`),
  });
  const people = useQuery({ queryKey: qk.people, queryFn: ticketsApi.people, staleTime: 5 * 60_000 });

  const [kind, setKind] = useState('');
  const [impact, setImpact] = useState('');
  const [locations, setLocations] = useState('');
  const [customers, setCustomers] = useState('');
  const [comms, setComms] = useState('');
  const [services, setServices] = useState('');
  const [managerId, setManagerId] = useState('');
  const [detectedAt, setDetectedAt] = useState('');
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!q.data || loaded) return;
    const e = q.data.entry;
    setKind(e?.outageKind ?? '');
    setImpact(e?.businessImpact ?? '');
    setLocations(e?.impactedLocations ?? '');
    setCustomers(e?.customersImpacted == null ? '' : String(e.customersImpacted));
    setComms(e?.commsChannel ?? '');
    setServices(e?.impactedServices ?? '');
    setManagerId(e?.managerId ?? '');
    setDetectedAt(e?.detectedAt ? toLocal(e.detectedAt) : '');
    setLoaded(true);
  }, [q.data, loaded]);

  const stored = q.data?.entry;
  const dirty =
    loaded &&
    (kind !== (stored?.outageKind ?? '') ||
      impact !== (stored?.businessImpact ?? '') ||
      locations !== (stored?.impactedLocations ?? '') ||
      customers !== (stored?.customersImpacted == null ? '' : String(stored.customersImpacted)) ||
      comms !== (stored?.commsChannel ?? '') ||
      services !== (stored?.impactedServices ?? '') ||
      managerId !== (stored?.managerId ?? '') ||
      detectedAt !== (stored?.detectedAt ? toLocal(stored.detectedAt) : ''));
  const clearDirty = useDirty(dirty);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.tickets.majorIncident(id) });
    // The review's publish gate reads the stand-down, so it has to re-ask.
    void qc.invalidateQueries({ queryKey: qk.tickets.pir(id) });
    void qc.invalidateQueries({ queryKey: qk.tickets.activity(id) });
  };

  /** Demotion is the same conversion in reverse — `#MI-1037` becomes
   *  `#INC-1037`, and the outage record stays put in case it is re-declared. */
  const demote = useMutation({
    mutationFn: () => apiSend(`/api/tickets/${id}`, 'PATCH', { type: 'incident' }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not change the type.');
      clearDirty();
      void qc.invalidateQueries({ queryKey: qk.tickets.detail(id) });
      refresh();
    },
  });

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      apiSend(`/api/tickets/${id}/major-incident`, 'PUT', patch),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save.');
      clearDirty();
      refresh();
    },
  });

  if (q.isPending) return <Skeleton className="m-4 h-64" />;
  if (q.isError)
    return (
      <div className="m-4 space-y-2 rounded-lg border p-4">
        <p className="text-sm font-medium text-destructive">Could not load the outage record</p>
        <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
          Retry
        </Button>
      </div>
    );

  const ran = outageDuration(stored?.declaredAt ?? null, stored?.stoodDownAt ?? null);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mx-auto w-full max-w-3xl min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {/* The two timestamps first: they are the facts, everything below is
          description. */}
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-destructive/15 text-destructive">
            <Megaphone className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1 text-sm">
            <p className="font-medium">
              {stored?.stoodDownAt ? 'Stood down' : 'Running'}
              {ran && <span className="font-normal text-muted-foreground"> · ran for {ran}</span>}
            </p>
            <p className="text-xs text-muted-foreground">
              Declared {stored?.declaredAt ? fullTime(stored.declaredAt) : 'unknown'}
              {stored?.stoodDownAt ? ` · service back ${fullTime(stored.stoodDownAt)}` : ''}
            </p>
          </div>
          {canEdit && (
            <Button
              size="sm"
              variant={stored?.stoodDownAt ? 'outline' : 'default'}
              disabled={save.isPending}
              onClick={() => save.mutate({ stoodDownAt: stored?.stoodDownAt ? null : Date.now() })}
            >
              <Radio className="h-4 w-4" />
              {stored?.stoodDownAt ? 'Still running' : 'Stand down'}
            </Button>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">How bad</label>
            <Select value={kind} onChange={(e) => setKind(e.target.value)} disabled={!canEdit}>
              <option value="">Not said</option>
              {OUTAGE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {OUTAGE_KIND_LABEL[k]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">People affected</label>
            <Input
              type="number"
              min={0}
              value={customers}
              onChange={(e) => setCustomers(e.target.value)}
              disabled={!canEdit}
              placeholder="Not counted"
            />
            {/* Blank and zero are different answers and the field says so —
              during an outage "we don't know yet" is the honest one. */}
            <p className="mt-1 text-[11px] text-muted-foreground">Leave blank if not counted yet.</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">War room</label>
            <Input
              value={comms}
              onChange={(e) => setComms(e.target.value)}
              disabled={!canEdit}
              placeholder="Teams link, bridge number…"
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Major incident manager
            </label>
            {/* Not the assignee. The assignee fixes it; the manager runs it,
              chairs the bridge and is who the business asks. Both reference
              exports print the two separately.

              Searchable (Kai): the options are every person in the directory,
              and a plain select is unusable past about thirty. Same control
              the assignee picker uses. */}
            <Combobox
              value={managerId || null}
              onChange={(v) => setManagerId(v ?? '')}
              /* Step 171 — staff only. An incident manager is a job on the
                 desk, and `/api/org/people` returns every kind, so without
                 this the picker offered every customer. */
              options={(people.data?.entries ?? [])
                .filter((a) => a.kind === 'agent' || a.kind === 'platform')
                .map((a) => ({
                  value: a.id,
                  label: a.name ?? a.email,
                }))}
              placeholder="Nobody yet"
              disabled={!canEdit}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">First detected</label>
            <Input
              type="datetime-local"
              value={detectedAt}
              onChange={(e) => setDetectedAt(e.target.value)}
              disabled={!canEdit}
              className="dark:[color-scheme:dark]"
            />
            {/* Earlier than raised, and the only input to mean-time-to-detect. */}
            <p className="mt-1 text-[11px] text-muted-foreground">When it was first seen, not raised.</p>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Business impact</label>
          <RichTextEditor value={impact} onChange={setImpact} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Impacted locations</label>
          <RichTextEditor value={locations} onChange={setLocations} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Impacted services</label>
          <RichTextEditor value={services} onChange={setServices} />
        </div>
      </div>
      {/* Docked, like the properties rail's Update (Kai). */}
      {canEdit && (
        <div
          className={cn(
            'flex shrink-0 flex-wrap items-center justify-end gap-2 border-t bg-card px-4 py-2.5',
          )}
        >
          {/* Kai: *"I can't undo declaring a major incident."* You could —
              through the Type picker in the rail, which nobody is going to
              find at 03:00 having just declared the wrong thing. Here it is
              plainly, and quietly: demotion keeps the number, the thread and
              every note (it is the same type conversion, in reverse), so
              nothing is lost by getting it wrong. */}
          <Button
            variant="ghost"
            size="sm"
            className="mr-auto text-muted-foreground"
            disabled={demote.isPending}
            onClick={async () => {
              if (
                !(await confirm({
                  title: 'Make this an ordinary incident again?',
                  body: 'Nothing written is lost — the outage record and review stay put.',
                  confirmLabel: 'Make it an incident',
                }))
              )
                return;
              demote.mutate();
            }}
          >
            <Undo2 className="h-3.5 w-3.5" />
            Not a major incident
          </Button>
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          <Button
            disabled={!dirty || save.isPending}
            onClick={() =>
              save.mutate({
                outageKind: kind || null,
                businessImpact: impact,
                impactedLocations: locations,
                // '' is "cleared" and must reach the API as null, not NaN.
                customersImpacted: customers.trim() === '' ? null : Number(customers),
                commsChannel: comms.trim() || null,
                impactedServices: services,
                managerId: managerId || null,
                detectedAt: detectedAt ? new Date(detectedAt).getTime() : null,
              })
            }
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}
